import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

// Mismo host, access_token fijo y endpoint de catálogo de agencias que ya
// usa usuarios-admin.js (GET /agencies?group=GoCar). El catálogo maestro de
// agencias vive ahí, no en cs_agencias.
const API_BASE_URL = "https://api.checkmark.mx/";
const ACCESS_TOKEN =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const getUserModules = () => JSON.parse(localStorage.getItem("modulos")) || [];
const getUserEmail = () => localStorage.getItem("userEmail") || "";

function AgenciasCotizadorAdmin() {
  const [agencias, setAgencias] = useState([]);
  const [userModules, setUserModules] = useState(getUserModules());
  const [userEmail, setUserEmail] = useState(getUserEmail());

  // Mismo patrón que app-menu.jsx: si el login se completó en otra pestaña,
  // o el localStorage se actualizó después de montar este componente, lo
  // recoge al volver el foco a la pestaña o al detectar el cambio.
  useEffect(() => {
    const refreshUserData = () => {
      setUserModules(getUserModules());
      setUserEmail(getUserEmail());
    };

    window.addEventListener("storage", refreshUserData);
    window.addEventListener("focus", refreshUserData);

    return () => {
      window.removeEventListener("storage", refreshUserData);
      window.removeEventListener("focus", refreshUserData);
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [savingAgId, setSavingAgId] = useState(null);

  const fetchAgencias = async () => {
    const response = await fetch(
      `${API_BASE_URL}agencies?access_token=${ACCESS_TOKEN}&group=GoCar`,
    );
    const result = await response.json();

    if (result.statusCode !== 200) {
      throw new Error(result.message || "Error al obtener agencias.");
    }

    return result?.data?.agencies || [];
  };

  const fetchConfigNr = async () => {
    const response = await fetch(
      `${API_BASE_URL}financiamiento/admin/agencias/cotizador`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: ACCESS_TOKEN,
          modulos: userModules,
          correo: userEmail,
        }),
      },
    );
    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.message || "Error al obtener la configuración NR.",
      );
    }

    return Array.isArray(result.data) ? result.data : [];
  };

  const cargarAgencias = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [agenciasData, configData] = await Promise.all([
        fetchAgencias(),
        fetchConfigNr(),
      ]);

      // Cruza por ag_id: catálogo completo de agencias + bandera NR guardada
      // (las que no tienen registro en financiamiento_agencia_config quedan
      // en false, que es el default seguro = flujo Babilonia).
      const configPorAgId = configData.reduce((acc, item) => {
        acc[String(item.ag_id)] = Boolean(item.usa_cotizador_nr);
        return acc;
      }, {});

      const combinadas = agenciasData.map((ag) => ({
        ag_id: String(ag.ag_id),
        nombre: ag.nombre,
        usa_cotizador_nr: configPorAgId[String(ag.ag_id)] || false,
      }));

      setAgencias(combinadas);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las agencias.");
    } finally {
      setLoading(false);
    }
  }, [userModules, userEmail]);

  useEffect(() => {
    cargarAgencias();
  }, [cargarAgencias]);

  const handleToggleNR = async (agId, nuevoValor) => {
    // Optimista: refleja el cambio de inmediato en la tabla.
    setAgencias((prev) =>
      prev.map((ag) =>
        ag.ag_id === agId ? { ...ag, usa_cotizador_nr: nuevoValor } : ag,
      ),
    );
    setSavingAgId(agId);

    try {
      const response = await fetch(
        `${API_BASE_URL}financiamiento/admin/agencias/cotizador/actualizar`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_token: ACCESS_TOKEN,
            modulos: userModules,
            correo: userEmail,
            ag_id: agId,
            usa_cotizador_nr: nuevoValor ? 1 : 0,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "No se pudo guardar el cambio.");
      }
    } catch (err) {
      // Revierte si falló el guardado.
      setAgencias((prev) =>
        prev.map((ag) =>
          ag.ag_id === agId ? { ...ag, usa_cotizador_nr: !nuevoValor } : ag,
        ),
      );
      alert(err.message || "No se pudo guardar el cambio.");
    } finally {
      setSavingAgId(null);
    }
  };

  const agenciasFiltradas = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return agencias;
    return agencias.filter(
      (ag) =>
        String(ag.nombre || "").toLowerCase().includes(term) ||
        String(ag.ag_id || "").toLowerCase().includes(term),
    );
  }, [agencias, busqueda]);

  const totalNR = agencias.filter((ag) => ag.usa_cotizador_nr).length;

  return (
    <div>
      <style>{`
        /* Blindaje contra el CSS global del tema (mismo criterio que
           nr-toolbar/nr-rfc-card en crear_venta.js): fuerza que el
           encabezado de la tabla sea legible y que el switch se vea
           como switch, sin depender de que las clases de Bootstrap
           ganen la pelea de especificidad contra el tema. */
        .acnr-table thead th {
          color: #333 !important;
          background-color: #f4f5f7 !important;
        }
        .acnr-switch {
          position: relative;
          display: inline-block;
          width: 42px !important;
          height: 24px !important;
          margin: 0 auto;
        }
        .acnr-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .acnr-switch-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc !important;
          transition: 0.15s;
          border-radius: 24px !important;
        }
        .acnr-switch-slider::before {
          position: absolute;
          content: "";
          height: 18px !important;
          width: 18px !important;
          left: 3px;
          bottom: 3px;
          background-color: #fff !important;
          border-radius: 50% !important;
          transition: 0.15s;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }
        .acnr-switch input:checked + .acnr-switch-slider {
          background-color: #2e7d32 !important;
        }
        .acnr-switch input:checked + .acnr-switch-slider::before {
          transform: translateX(18px);
        }
        .acnr-switch input:disabled + .acnr-switch-slider {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      <h1 className="page-header">
        Cotizador por agencia
        <small>NR Procotiza vs flujo propio de Babilonia</small>
      </h1>

      <div className="card mb-3">
        <div className="card-body">
          <p className="mb-2">
            Activa el cotizador NR únicamente para las agencias que ya tienen
            convenio con NR Finance. Las agencias apagadas (o sin
            configurar) siguen usando el flujo normal de Babilonia sin
            ningún cambio.
          </p>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <span className="badge bg-theme">
              {totalNR} agencia{totalNR === 1 ? "" : "s"} con NR activado
            </span>
            <span className="text-muted">
              {agencias.length} agencias en total
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: "320px" }}
              placeholder="Buscar agencia..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-theme btn-sm"
              onClick={cargarAgencias}
              disabled={loading}
            >
              <i className="fa fa-rotate-right me-1"></i>
              Actualizar
            </button>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <div className="text-center text-muted py-4">Cargando...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle acnr-table">
                <thead>
                  <tr>
                    <th>Agencia</th>
                    <th>ag_id</th>
                    <th className="text-center" style={{ width: "160px" }}>
                      Usa cotizador NR
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {agenciasFiltradas.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-4">
                        No se encontraron agencias.
                      </td>
                    </tr>
                  ) : (
                    agenciasFiltradas.map((ag) => (
                      <tr key={ag.ag_id}>
                        <td>{ag.nombre}</td>
                        <td className="text-muted small">{ag.ag_id}</td>
                        <td className="text-center">
                          <label className="acnr-switch">
                            <input
                              type="checkbox"
                              checked={Boolean(ag.usa_cotizador_nr)}
                              disabled={savingAgId === ag.ag_id}
                              onChange={(e) =>
                                handleToggleNR(ag.ag_id, e.target.checked)
                              }
                            />
                            <span className="acnr-switch-slider"></span>
                          </label>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgenciasCotizadorAdmin;
